import React from 'react';

/**
 * Badge Component - Consistent badge styling for status indicators
 * 
 * Variants:
 * - primary: Orange theme
 * - success: Green theme
 * - warning: Yellow theme
 * - error: Red theme
 * - info: Blue theme
 * - neutral: Gray theme
 */

interface BadgeProps {
  variant?: 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
}

const Badge: React.FC<BadgeProps> = ({
  variant = 'neutral',
  size = 'md',
  children,
  className = '',
  icon
}) => {
  const baseClasses = [
    'inline-flex items-center font-medium rounded-full',
    'select-none whitespace-nowrap'
  ];

  const variantClasses = {
    primary: 'bg-orange-100 text-orange-800',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
    info: 'bg-blue-100 text-blue-800',
    neutral: 'bg-gray-100 text-gray-800'
  };

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs gap-1',
    md: 'px-2.5 py-1 text-sm gap-1.5',
    lg: 'px-3 py-1.5 text-sm gap-2'
  };

  const combinedClasses = [
    ...baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    className
  ].join(' ');

  return (
    <span className={combinedClasses}>
      {icon && icon}
      {children}
    </span>
  );
};

export default Badge;
